/**
 * DegreeSEO — one component that owns every SEO surface on a course
 * page: <title>, meta description, canonical link, and the OG /
 * Twitter card meta tags social scrapers read. Rendered via
 * react-helmet-async so all tags land in <head> (HelmetProvider is
 * already wired at the app root in src/index.js).
 *
 * Schema.org JSON-LD is injected separately by DegreeDetail (fetching
 * /api/public/degrees/{id}/schema-org). This component is the twin
 * that handles the tags NOT covered by JSON-LD.
 *
 * SEO surfaces populated:
 *   - `<title>` — "{Course name} · {University} · WUA"
 *   - `<meta name="description">` — first ~155 chars of The Professor
 *     summary (or a curated fallback).
 *   - `<link rel="canonical">` — absolute production URL (fixes
 *     duplicate-content signals when the preview URL is indexed).
 *   - Open Graph (`og:title|description|image|url|type|site_name`)
 *     for Facebook, LinkedIn, Slack.
 *   - Twitter card (`twitter:card|title|description|image`) — same
 *     data, distinct namespace.
 *
 * Feb 2026 · SEO Phase 4.
 */
import { Helmet } from "react-helmet-async";

const SITE = "https://whichuniversityadvisor.com";
const DEFAULT_OG = `${SITE}/og/course-default.jpg`;

/** Trim to ~155 chars on a word boundary — SERP snippet sweet spot. */
function _snippet(s, max = 155) {
  if (!s) return "";
  const str = String(s).trim().replace(/\s+/g, " ");
  if (str.length <= max) return str;
  const cut = str.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trimEnd() + "…";
}

export default function DegreeSEO({ degree, uni, aiSummary }) {
  if (!degree) return null;

  const courseName = degree.official_title || degree.name || degree.title;
  const uniName = uni?.name || degree.university_name || "";
  const canonical = `${SITE}/degree/${degree.id}`;
  const title = uniName
    ? `${courseName} · ${uniName} · WUA`
    : `${courseName} · WUA`;

  const description = _snippet(
    aiSummary
      || degree.description
      || `${courseName}${uniName ? " at " + uniName : ""}. Compare admissions, cost, teaching quality and graduate outcomes on WhichUniversityAdvisor.`,
  );

  // OG image precedence: uni gallery (real Google Places photo) →
  // uni.photo_url → WUA branded fallback. Absolute HTTPS always so
  // Twitter/Facebook can fetch cross-origin.
  const galleryFirst =
    Array.isArray(uni?.gallery) && uni.gallery.length > 0 ? uni.gallery[0] : null;
  const galleryUrl =
    galleryFirst && typeof galleryFirst === "object"
      ? galleryFirst.url || galleryFirst.src
      : (typeof galleryFirst === "string" ? galleryFirst : null);
  const ogImage = galleryUrl || uni?.photo_url || DEFAULT_OG;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="WhichUniversityAdvisor" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      {/* Twitter card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}
